// src/pages/earningsImport/POSUploadHistory.js
import React, { useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../firebase/firebase";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(value) {
  const d = toDate(value);
  return d
    ? d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "-";
}

function money(value) {
  return Number(value || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function exactLegacyMatch(a, b) {
  return (
    String(a?.terminalId || "") === String(b?.terminalId || "") &&
    String(a?.date || "") === String(b?.date || "") &&
    Number(a?.amount || 0).toFixed(2) === Number(b?.amount || 0).toFixed(2) &&
    String(a?.carId || "") === String(b?.carId || "")
  );
}

async function commitDeletes(refs) {
  let batch = writeBatch(db);
  let count = 0;
  for (const ref of refs) {
    batch.delete(ref);
    count += 1;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count) await batch.commit();
}

function Button({ children, tone = "primary", disabled, ...props }) {
  const colors = { primary: "#2563eb", warning: "#d97706", danger: "#dc2626", neutral: "#475569" };
  return (
    <button
      {...props}
      disabled={disabled}
      style={{
        background: disabled ? "#94a3b8" : colors[tone],
        color: "white",
        border: 0,
        borderRadius: 8,
        padding: "9px 14px",
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        marginRight: 8,
      }}
    >
      {children}
    </button>
  );
}

export default function POSUploadHistory() {
  const [month, setMonth] = useState(currentMonth());
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");

  const previewTotal = useMemo(
    () => (preview?.auditRows || []).reduce((sum, row) => sum + Number(row.data?.amount || 0), 0),
    [preview]
  );

  async function loadHistory() {
    setLoading(true);
    setMessage("");
    setPreview(null);
    try {
      const snap = await getDocs(collection(db, "posUploads", month, "uploads"));
      const rows = snap.docs
        .map((d) => ({ id: d.id, ref: d.ref, ...d.data() }))
        .sort((a, b) => (toDate(b.uploadedAt)?.getTime() || 0) - (toDate(a.uploadedAt)?.getTime() || 0));
      setUploads(rows);
      if (!rows.length) setMessage(`No POS upload history found for ${month}.`);
    } catch (error) {
      setMessage(`Failed to load POS history: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  }

  async function previewBatch(upload) {
    setPreviewLoading(true);
    setPreview(null);
    setDeleteText("");
    setMessage("");

    try {
      const auditSnap = await getDocs(collection(db, "posUploads", month, "uploads", upload.id, "transactions"));
      const auditRows = auditSnap.docs.map((d) => ({ id: d.id, ref: d.ref, data: d.data() }));

      const recordMatches = new Map();
      // New uploads: fast and exact through uploadId.
      const directSnap = await getDocs(
        query(collection(db, "posUploads", month, "records"), where("uploadId", "==", upload.id))
      );
      directSnap.docs.forEach((d) => recordMatches.set(d.ref.path, { id: d.id, ref: d.ref, data: d.data(), matchType: "uploadId" }));

      // Legacy uploads did not store uploadId. Match their audit rows carefully.
      if (recordMatches.size === 0 && auditRows.length) {
        const recordsSnap = await getDocs(collection(db, "posUploads", month, "records"));
        const available = recordsSnap.docs.map((d) => ({ id: d.id, ref: d.ref, data: d.data() }));
        const used = new Set();
        for (const audit of auditRows) {
          const exactKey = audit.data?.transactionKey;
          let found = exactKey ? available.find((r) => !used.has(r.ref.path) && (r.id === exactKey || r.data?.transactionKey === exactKey)) : null;
          if (!found) found = available.find((r) => !used.has(r.ref.path) && exactLegacyMatch(r.data, audit.data));
          if (found) {
            used.add(found.ref.path);
            recordMatches.set(found.ref.path, { ...found, matchType: exactKey ? "transactionKey" : "legacy fields" });
          }
        }
      }

      setPreview({ upload, auditRows, recordRows: Array.from(recordMatches.values()) });
    } catch (error) {
      alert(`Preview failed: ${error.message || error}`);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function deleteBatch() {
    if (!preview) return;
    if (deleteText.trim() !== preview.upload.id) {
      alert("Type the exact POS Upload ID before deleting.");
      return;
    }

    const ok = window.confirm(
      `Delete POS batch ${preview.upload.id}?\n\nThis will delete ${preview.recordRows.length} monthly POS records, ${preview.auditRows.length} audit rows, and the upload history document.`
    );
    if (!ok) return;

    setDeleting(true);
    try {
      const refs = [...preview.recordRows.map((r) => r.ref), ...preview.auditRows.map((r) => r.ref)];
      await commitDeletes(refs);
      await deleteDoc(doc(db, "posUploads", month, "uploads", preview.upload.id));

      await addDoc(collection(db, "deletedPOSUploads"), {
        month,
        uploadId: preview.upload.id,
        fileName: preview.upload.fileName || "",
        deletedRecordCount: preview.recordRows.length,
        deletedAuditCount: preview.auditRows.length,
        deletedAt: new Date(),
        deletedBy: auth?.currentUser?.email || "unknown",
        source: "POSUploadHistory",
      });

      setMessage(`Deleted POS batch ${preview.upload.id}: ${preview.recordRows.length} records removed.`);
      setPreview(null);
      setDeleteText("");
      await loadHistory();
    } catch (error) {
      alert(`Delete failed: ${error.message || error}`);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 1350, margin: "0 auto", fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ marginBottom: 6 }}>POS Upload History</h2>
      <p style={{ color: "#475569", marginTop: 0 }}>Preview a POS upload and safely delete the full batch from both upload audit and monthly records.</p>

      <div style={card}>
        <label style={{ fontWeight: 700, marginRight: 10 }}>Month</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={inputStyle} />
        <Button onClick={loadHistory} disabled={loading}>{loading ? "Loading..." : "Load History"}</Button>
      </div>

      {message && <div style={{ ...card, background: "#eff6ff", color: "#1e3a8a" }}>{message}</div>}

      <div style={card}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead><tr style={{ background: "#f8fafc" }}>
              <th style={th}>Uploaded At</th><th style={th}>File</th><th style={th}>Upload ID</th><th style={th}>Original</th>
              <th style={th}>Saved</th><th style={th}>Duplicates</th><th style={th}>Unmatched</th><th style={th}>Errors</th><th style={th}>Action</th>
            </tr></thead>
            <tbody>
              {uploads.map((u) => <tr key={u.id}>
                <td style={td}>{formatDateTime(u.uploadedAt)}</td><td style={td}>{u.fileName || "-"}</td>
                <td style={{ ...td, fontFamily: "monospace" }}>{u.id}</td><td style={td}>{u.originalSize ?? "-"}</td>
                <td style={td}>{u.savedCount ?? "-"}</td><td style={td}>{u.duplicateCount ?? 0}</td>
                <td style={td}>{u.unmatchedCount ?? 0}</td><td style={td}>{u.errorCount ?? u.errors?.length ?? 0}</td>
                <td style={td}><Button tone="warning" onClick={() => previewBatch(u)} disabled={previewLoading}>Preview Delete</Button></td>
              </tr>)}
              {!uploads.length && <tr><td colSpan={9} style={{ ...td, textAlign: "center", color: "#64748b" }}>No history loaded.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {preview && <div style={{ ...card, border: "2px solid #2563eb" }}>
        <h3 style={{ marginTop: 0 }}>Delete Preview</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 10 }}>
          <Info label="Upload ID" value={preview.upload.id} mono />
          <Info label="File" value={preview.upload.fileName || "-"} />
          <Info label="Audit rows" value={preview.auditRows.length} />
          <Info label="Monthly records found" value={preview.recordRows.length} />
          <Info label="Audit total" value={`AED ${money(previewTotal)}`} />
          <Info label="Saved in history" value={preview.upload.savedCount ?? "-"} />
          <Info label="Duplicates" value={preview.upload.duplicateCount ?? 0} />
          <Info label="Errors" value={preview.upload.errorCount ?? preview.upload.errors?.length ?? 0} />
        </div>

        {preview.auditRows.filter((row) => row.data?.saveStatus !== "duplicate").length !== preview.recordRows.length && <div style={{ marginTop: 14, padding: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, color: "#9a3412" }}>
          Saved audit rows and monthly records are not equal. This indicates a legacy or partially saved batch. Deletion will remove every monthly record that can be matched, plus all audit rows.
        </div>}

        <div style={{ overflowX: "auto", maxHeight: 330, marginTop: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ background: "#f8fafc" }}><th style={th}>Date</th><th style={th}>Terminal</th><th style={th}>Car</th><th style={th}>Amount</th><th style={th}>Record status</th></tr></thead>
            <tbody>{preview.auditRows.map((a) => {
              const found = preview.recordRows.find((r) =>
                r.id === a.data?.recordId ||
                r.id === a.id ||
                r.data?.auditTransactionId === a.id ||
                r.data?.transactionKey === a.data?.transactionKey ||
                exactLegacyMatch(r.data, a.data)
              );
              const status = a.data?.saveStatus === "duplicate"
                ? "Duplicate (no monthly record created)"
                : found
                  ? `Found (${found.matchType})`
                  : "Missing from records";
              return <tr key={a.ref.path}><td style={td}>{a.data.date}</td><td style={td}>{a.data.terminalId}</td><td style={td}>{a.data.carNumber}</td><td style={td}>{money(a.data.amount)}</td><td style={td}>{status}</td></tr>;
            })}</tbody>
          </table>
        </div>

        <div style={{ marginTop: 16, padding: 14, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10 }}>
          <strong style={{ color: "#991b1b" }}>Type the exact Upload ID to confirm:</strong><br />
          <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder={preview.upload.id} style={{ ...inputStyle, width: 330, marginTop: 10 }} />
          <Button tone="danger" onClick={deleteBatch} disabled={deleting || deleteText.trim() !== preview.upload.id}>{deleting ? "Deleting..." : "Delete Full POS Batch"}</Button>
          <Button tone="neutral" onClick={() => setPreview(null)} disabled={deleting}>Cancel</Button>
        </div>
      </div>}
    </div>
  );
}

function Info({ label, value, mono }) {
  return <div style={{ border: "1px solid #e2e8f0", borderRadius: 9, padding: 10 }}><div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>{label}</div><div style={{ marginTop: 5, fontFamily: mono ? "monospace" : "inherit", fontWeight: 700 }}>{value}</div></div>;
}

const card = { background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 18 };
const inputStyle = { border: "1px solid #cbd5e1", borderRadius: 8, padding: "9px 10px", marginRight: 10 };
const th = { textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
const td = { padding: "9px 8px", borderBottom: "1px solid #e2e8f0", verticalAlign: "top" };
